import { Logger } from "@/lib/logger";

const log = new Logger("Device");

/**
 * Environnement où la visioconférence est connue pour mal fonctionner (§3.10 du rapport client).
 *
 * On vise les **WebView d'application** — l'élève ouvre le lien depuis Gmail, l'app Google,
 * Facebook ou Instagram, et se retrouve dans un navigateur embarqué aux capacités WebRTC
 * amputées. Cas mesuré le 23/07 : un élève sur l'app Google publiait son micro sans caméra, avec
 * jusqu'à 100 % de perte de paquets.
 *
 * On ne bloque rien et on n'inclut volontairement pas les navigateurs alternatifs de bureau
 * (Edge, Avast, MIUI) : les relevés montrent qu'ils publient correctement. Un avertissement de
 * trop pousserait des familles à changer d'outil sans raison.
 */
export function isUnsupportedWebView(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;

  // WebView iOS : ni Safari (navigateur complet) ni les navigateurs tiers connus, mais un moteur
  // WebKit embarqué. Les apps ajoutent leur propre marqueur (GSA = Google Search App).
  if (isIosWebkit()) {
    if (/GSA\/|FBAN|FBAV|Instagram|Line\/|Twitter/i.test(ua)) return true;
    // Safari complet annonce "Safari/" ; les navigateurs tiers iOS annoncent CriOS/FxiOS/EdgiOS.
    const isKnownBrowser = /Safari\/|CriOS\/|FxiOS\/|EdgiOS\/|OPiOS\//.test(ua);
    return !isKnownBrowser;
  }

  // WebView Android : marqueur "; wv" dans l'UA, ou les apps qui s'identifient explicitement.
  if (/Android/.test(ua)) {
    return /;\s*wv\)/.test(ua) || /FBAN|FBAV|Instagram|Line\//i.test(ua);
  }

  return false;
}

/**
 * WebView de notre propre application mobile (UA marqué "succeschoolapp"), par opposition aux
 * navigateurs embarqués tiers détectés par `isUnsupportedWebView`. Même limite de moteur WebView
 * pour le téléchargement d'URL blob (`<a download>` ignoré), mais on ignore si l'app expose un
 * moyen d'ouvrir la page dans un vrai navigateur — le message de repli doit rester générique.
 */
export function isOwnAppWebView(): boolean {
  if (typeof navigator === "undefined") return false;
  return /succeschoolapp/i.test(navigator.userAgent);
}

/** Détecte iOS/iPadOS (y compris iPadOS 13+ qui se présente comme "MacIntel"). */
export function isIosWebkit(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIphoneOrIpad = /iPad|iPhone|iPod/.test(ua);
  const isIpadOsAsMac = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const result = isIphoneOrIpad || isIpadOsAsMac;
  return result;
}

/**
 * Détecte si l'appareil est sous iOS / iPadOS.
 * Logue le résultat en interne.
 */
export function isIos(): boolean {
  const result = isIosWebkit();
  log.debug("isIos check:", { result });
  return result;
}

/**
 * Détecte si l'application s'exécute en mode PWA / Standalone (lancée depuis l'écran d'accueil).
 * Logue le résultat en interne.
 */
export function isStandalone(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;

  const nav = navigator as unknown as { standalone?: boolean };
  const isIosStandalone = Boolean(nav.standalone);
  const isMediaStandalone =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;

  const result = isIosStandalone || isMediaStandalone;
  log.debug("isStandalone check:", { result, isIosStandalone, isMediaStandalone });
  return result;
}

/** Alias pour `isStandalone` */
export function isPwa(): boolean {
  return isStandalone();
}

/**
 * Détecte si la page s'exécute dans une WebView (soit notre app interne, soit un navigateur embarqué tiers).
 * Logue le résultat en interne.
 */
export function isWebView(): boolean {
  const isOwn = isOwnAppWebView();
  const isUnsupported = isUnsupportedWebView();
  const result = isOwn || isUnsupported;
  log.debug("isWebView check:", { result, isOwn, isUnsupported });
  return result;
}

/**
 * Détecte si l'utilisateur navigue sur iOS Safari standard (hors standalone, hors WebView).
 * Utilisé pour déterminer l'éligibilité au prompt d'ajout à l'écran d'accueil.
 */
export function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ios = isIos();
  const standalone = isStandalone();
  const webView = isWebView();
  const result = ios && !standalone && !webView;
  log.debug("isIosSafari check:", { result, ios, standalone, webView });
  return result;
}

/**
 * Appareil susceptible de saturer au décodage de nombreux flux vidéo simultanés (anciens iPad,
 * téléphones à faible mémoire). Volontairement large : sur un mobile ou une tablette, afficher
 * quinze vignettes minuscules n'apporte rien, alors que le coût de décodage, lui, est bien réel.
 * `navigator.deviceMemory` n'est pas exposé par Safari — on ne peut compter que sur l'UA et le
 * nombre de cœurs.
 */
export function isLowPoweredDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  if (isIosWebkit() || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) return true;
  const cores = navigator.hardwareConcurrency;
  return typeof cores === "number" && cores > 0 && cores <= 4;
}

/**
 * Version majeure du moteur Safari/WebKit, ou `null` hors WebKit.
 *
 * Attention : Chrome, Edge et consorts embarquent « Safari » dans leur UA sur desktop — on ne
 * peut pas se contenter d'y chercher « Safari ». Sur iOS en revanche tous les navigateurs sont
 * WebKit, mais les navigateurs tiers (CriOS, FxiOS) n'annoncent pas de `Version/` : on retombe
 * alors sur la version d'iOS, qui suit la même numérotation que le moteur.
 */
export function getWebkitMajorVersion(): number | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  const isWebkit = isIosWebkit() || (/Safari\//.test(ua) && !/Chrome|Chromium|Android/.test(ua));
  if (!isWebkit) return null;

  const appVersion = ua.match(/Version\/(\d+)/);
  if (appVersion) return Number(appVersion[1]);

  // « Mac OS X 10_15_7 » ne matche pas : après « OS » vient « X », pas un chiffre.
  const osVersion = ua.match(/OS (\d+)[_.]/);
  return osVersion ? Number(osVersion[1]) : null;
}

/**
 * Seuil du profil de repli WebKit. Safari 18 est la première version où les sessions observées
 * en production se comportent normalement ; les incidents mesurés le 21/07 (caméra publiée à
 * 0 fps, réception qui s'effondre à zéro) proviennent tous d'un iPad en Safari 17.
 */
export const LEGACY_WEBKIT_MAX_VERSION = 18;

/** WebKit trop ancien pour les réglages vidéo par défaut (voir `LEGACY_WEBKIT_MAX_VERSION`). */
export function isLegacyWebkit(): boolean {
  const major = getWebkitMajorVersion();
  return major !== null && major < LEGACY_WEBKIT_MAX_VERSION;
}
